// src/components/ShippingBadge.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Shows shipping availability status on product cards and detail pages.
// Uses fields returned by shopController:
//   product.unavailableInVisitorCountry  → true if no local/GL listing for visitor
//   product.shipsTo                      → ['WORLDWIDE'] | ['US','CA',...]
//   product.shipFromCountry              → 'US' | 'VN' | ''
//   product.isGlobalListing              → true if this is the GL listing
//   product.country                      → listing country code
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { getCountry } from '../utils/countryDetect';

// ── Country name lookup ───────────────────────────────────────────────────────
const COUNTRY_NAMES = {
  US:'United States', VN:'Vietnam', SG:'Singapore', JP:'Japan',
  AU:'Australia', GB:'United Kingdom', CA:'Canada', DE:'Germany',
  FR:'France', KR:'South Korea', TH:'Thailand', MY:'Malaysia',
  ID:'Indonesia', PH:'Philippines', AE:'UAE', CN:'China', GL:'Global',
};

const countryName = (code) => COUNTRY_NAMES[code] || code;

// ── Main badge component ──────────────────────────────────────────────────────
export function ShippingBadge({ product, size = 'sm' }) {
  if (!product) return null;

  const visitorCountry = getCountry();
  const shipsTo        = product.shipsTo || ['WORLDWIDE'];
  const shipFrom       = product.shipFromCountry || '';

  // Determine what to show
  let type    = null;   // 'unavailable' | 'local-only' | 'ships-from' | null
  let label   = '';
  let tooltip = '';

  if (product.unavailableInVisitorCountry) {
    // Product shown as fallback — doesn't ship to visitor's country
    type    = 'unavailable';
    const countries = shipsTo.includes('WORLDWIDE') ? [] : shipsTo;
    label   = countries.length > 0
      ? `Ships to ${countries.slice(0, 3).map(countryName).join(', ')}${countries.length > 3 ? ` +${countries.length - 3}` : ''}`
      : 'Limited shipping';
    tooltip = `This product may not ship to ${countryName(visitorCountry)}`;

  } else if (!shipsTo.includes('WORLDWIDE') && shipsTo.length > 0 && !shipsTo.includes(visitorCountry)) {
    // Has explicit shipsTo but visitor's country is somehow in it via GL fallback
    type    = 'local-only';
    label   = `Ships to ${shipsTo.slice(0, 2).map(countryName).join(', ')}`;
    tooltip = label;

  } else if (shipFrom && shipFrom !== visitorCountry) {
    // Ships from a different country — useful info for delivery estimate
    type    = 'ships-from';
    label   = `Ships from ${countryName(shipFrom)}`;
    tooltip = `International shipping from ${countryName(shipFrom)}`;
  }

  if (!type) return null;

  const styles = {
    unavailable: {
      background: 'rgba(186,117,23,0.12)',
      color:      '#FAC775',
      border:     '1px solid rgba(186,117,23,0.3)',
    },
    'local-only': {
      background: 'rgba(55,138,221,0.1)',
      color:      '#85B7EB',
      border:     '1px solid rgba(55,138,221,0.25)',
    },
    'ships-from': {
      background: 'rgba(255,255,255,0.05)',
      color:      'rgba(224,240,240,0.5)',
      border:     '1px solid rgba(255,255,255,0.1)',
    },
  };

  const fontSize = size === 'sm' ? 11 : 13;
  const padding  = size === 'sm' ? '2px 7px' : '4px 10px';

  return (
    <span
      title={tooltip}
      style={{
        display:      'inline-flex',
        alignItems:   'center',
        gap:          4,
        fontSize,
        padding,
        borderRadius: 99,
        fontWeight:   500,
        lineHeight:   1.4,
        cursor:       'default',
        ...styles[type],
      }}
    >
      {type === 'unavailable' && (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
          <circle cx="5" cy="5" r="4.5" stroke="#BA7517" strokeWidth="1"/>
          <path d="M5 3v2.5M5 7h.01" stroke="#BA7517" strokeWidth="1.2" strokeLinecap="round"/>
        </svg>
      )}
      {label}
    </span>
  );
}

// ── Compact icon-only variant for tight spaces ────────────────────────────────
export function ShippingIcon({ product }) {
  if (!product?.unavailableInVisitorCountry) return null;

  return (
    <span
      title="Limited shipping availability"
      style={{
        display:        'inline-flex',
        alignItems:     'center',
        justifyContent: 'center',
        width:          20,
        height:         20,
        borderRadius:   '50%',
        background:     '#FAEEDA',
        border:         '1px solid #EF9F27',
        cursor:         'default',
        flexShrink:     0,
      }}
    >
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <circle cx="5" cy="5" r="4.5" stroke="#FAC775" strokeWidth="1"/>
        <path d="M5 3v2.5M5 7h.01" stroke="#FAC775" strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
    </span>
  );
}

export default ShippingBadge;


// ─────────────────────────────────────────────────────────────────────────────
// HOW TO USE IN YOUR COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

/*

── ProductCard.jsx ──────────────────────────────────────────────────────────────

import { ShippingBadge } from '../components/ShippingBadge';

function ProductCard({ product, onAdd }) {
  return (
    <div className="product-card">
      <img src={product.primaryImage?.url} alt={product.name} />

      <div className="product-info">
        <h3>{product.name}</h3>

        // Add badge below product name
        <ShippingBadge product={product} size="sm" />

        <div className="price">
          {product.onSale && <span className="original">{product.originalPrice}</span>}
          <span className="current">{product.currentPrice} {product.currency}</span>
        </div>

        <button
          onClick={() => onAdd(product)}
          disabled={product.unavailableInVisitorCountry}
          style={{ opacity: product.unavailableInVisitorCountry ? 0.5 : 1 }}
        >
          {product.unavailableInVisitorCountry ? 'Unavailable in your region' : 'Add to Cart'}
        </button>
      </div>
    </div>
  );
}


── ProductDetailPage.jsx ────────────────────────────────────────────────────────

import { ShippingBadge } from '../components/ShippingBadge';

// In the product detail section, after price display:
<ShippingBadge product={product} size="lg" />

// Disable add-to-cart if unavailable
<button
  onClick={() => onAdd(product)}
  disabled={product.unavailableInVisitorCountry || !product.inStock}
>
  {product.unavailableInVisitorCountry
    ? 'Not available in your region'
    : !product.inStock
    ? 'Out of stock'
    : 'Add to Cart'}
</button>

// Show available countries from the availableIn list returned by getProductDetail:
{product.availableIn?.length > 0 && (
  <div style={{ fontSize: 13, color: '#888', marginTop: 8 }}>
    Available in:{' '}
    {product.availableIn.map(l => l.country).join(', ')}
  </div>
)}

*/